import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    fetchAllData,
    fetchData,
    fetchUpdateData,
} from "../../helpers/externapi";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Layout from "../../Layout/Layout";
import PageWrapper from "../../Components/PageWrapper";

export default function SPOCManagement() {
    const navigate = useNavigate();
    const { Id: hospitalId, spocId } = useParams(); // Added spocId for edit mode
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [spocErrors, setSpocErrors] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);

    const UserId = localStorage.getItem("UserId");

    const [spocFormData, setSpocFormData] = useState({
        FullName: "",
        MobileNumber: "",
        DateOfBirth: "",
        Age: "",
        Email: "",
        Address: "",
        Qualification: "",
        IsPrimary: false,
        IsSecondary: false,
        hospitalContactId: "",
    });

    // Check if it's edit mode and fetch existing SPOC
    useEffect(() => {
        if (spocId) {
            setIsEditMode(true);
            fetchExistingSPOC();
        }
    }, [spocId]);

  

    const fetchExistingSPOC = async () => {
        try {
            setLoading(true);
            const spocResponse = await fetchAllData(`HospitalContact/GetById/${spocId}`);

            if (spocResponse[0]) {
                setSpocFormData({
                    FullName: spocResponse[0].FullName || "",
                    MobileNumber: spocResponse[0].MobileNumber || "",
                    DateOfBirth: spocResponse[0].DateofBirth ? spocResponse[0].DateofBirth.split("T")[0] : "",
                    Age: spocResponse[0].Age || "",
                    Email: spocResponse[0].Email || "",
                    Address: spocResponse[0].Address || "",
                    Qualification: spocResponse[0].Qualification || "",
                    IsPrimary: spocResponse[0].IsPrimary || false,
                    IsSecondary: spocResponse[0].IsSecondary || false,
                    hospitalContactId: spocResponse[0].HospitalContactId || "",
                });
            }
        } catch (error) {
            console.error("Error fetching SPOC data:", error);
            setSnackbarMessage("Error fetching SPOC data");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const calculateAge = (dob) => {
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const month = today.getMonth() - birthDate.getMonth();
        if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const calculateDOBFromAge = (age) => {
        const today = new Date();
        return new Date(today.setFullYear(today.getFullYear() - age))
            .toISOString()
            .split("T")[0];
    };

    const handleInputChange = (field, value) => {
        const updatedSpocFormData = { ...spocFormData };

        if (field === "Age") {
            updatedSpocFormData.Age = value;
            updatedSpocFormData.DateOfBirth = value
                ? calculateDOBFromAge(parseInt(value))
                : "";
        } else if (field === "DateOfBirth") {
            updatedSpocFormData.DateOfBirth = value;
            updatedSpocFormData.Age = value ? calculateAge(value) : "";
        } else {
            updatedSpocFormData[field] = value;
        }

        setSpocFormData(updatedSpocFormData);
    };

    const validateSpocBeforeSubmit = () => {
        const errors = {};
        
        if (!spocFormData.FullName?.trim()) {
            errors.FullName = "Please Enter SPOC Name";
        }

        if (
            typeof spocFormData.MobileNumber === "string" &&
            spocFormData.MobileNumber.trim() === ""
        ) {
            errors.MobileNumber = "Please enter a valid 10-digit mobile number";
        } else if (
            !/^[6-9]\d{9}$/.test(spocFormData.MobileNumber && spocFormData.MobileNumber.trim())
        ) {
            errors.MobileNumber =
                "Mobile Number must start with 6, 7, 8, or 9 and must be 10 digits";
        }

        setSpocErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const onSubmitHandler = async (event) => {
        event.preventDefault();

        const isSpocValid = validateSpocBeforeSubmit();

        if (!isSpocValid) {
            setSnackbarMessage("Please fix the validation errors");
            setSnackbarOpen(true);
            return;
        }

        try {
            setSubmitLoading(true);

            if (isEditMode) {
                // Update existing SPOC
                const updatePayload = {
                    FullName: spocFormData.FullName,
                    MobileNumber: spocFormData.MobileNumber,
                    DateofBirth: spocFormData.DateOfBirth,
                    Age: spocFormData.Age,
                    Email: spocFormData.Email,
                    Address: spocFormData.Address,
                    Qualification: spocFormData.Qualification,
                    IsPrimary: spocFormData.IsPrimary,
                    IsSecondary: spocFormData.IsSecondary,
                    HospitalId: hospitalId,
                    hospitalContactId: spocFormData.hospitalContactId,
                    UpdatedBy: UserId,
                };

                await fetchData("HospitalContact/seedUpdate", [updatePayload]);
                setSnackbarMessage("SPOC updated successfully!");
            } else {
                // Create new SPOC
                const createPayload = {
                    FullName: spocFormData.FullName,
                    MobileNumber: spocFormData.MobileNumber,
                    DateofBirth: spocFormData.DateOfBirth,
                    Age: spocFormData.Age,
                    Email: spocFormData.Email,
                    Address: spocFormData.Address,
                    Qualification: spocFormData.Qualification,
                    IsPrimary: spocFormData.IsPrimary,
                    IsSecondary: spocFormData.IsSecondary,
                    HospitalId: hospitalId,
                    CreatedBy: UserId,
                };

                await fetchData("HospitalContact/seed", [createPayload]);
                setSnackbarMessage("SPOC added successfully!");
            }

            setSnackbarOpen(true);

            // Navigate back to hospital details after 2 seconds
            setTimeout(() => {
                navigate(`/hospitals/details/${hospitalId}`);
            }, 2000);

        } catch (error) {
            console.error("Error during SPOC data submission:", error);
            setSnackbarMessage("Submission Failed");
            setSnackbarOpen(true);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbarOpen(false);
    };

    if (loading) {
        return (
            <Layout>
                <PageWrapper>
                    <div className="container-fluid mt-5">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    </div>
                </PageWrapper>
            </Layout>
        );
    }

    return (
        <>
            <Layout>
                <PageWrapper>
                    <div className="container-fluid mt-5">
                        <div className="row">
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div
                                        className="card-header p-3 text-white"
                                        style={{
                                            background: "linear-gradient(to right, #1976D2, #0D47A1)",
                                        }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="rounded-circle me-3 p-2 d-flex align-items-center justify-content-center"
                                                    style={{
                                                        backgroundColor: "#ECEFF1",
                                                        width: "36px",
                                                        height: "36px",
                                                    }}
                                                >
                                                    <i className="bx bx-group text-primary"></i>
                                                </div>
                                                <h5 className="mb-0 fw-bold text-white">
                                                    {isEditMode ? "EDIT SPOC" : "ADD SPOC"}
                                                </h5>
                                            </div>
                                            <button
                                                className="btn btn-light btn-sm"
                                                onClick={() => navigate(`/hospitals/details/${hospitalId}`)}
                                            >
                                                <i className="bx bx-arrow-back me-1"></i>
                                                Back to Hospital
                                            </button>
                                        </div>
                                    </div>

                                    <div className="card-body p-4">
                                        <form onSubmit={onSubmitHandler}>
                                            <div className="border rounded p-4 mb-4" style={{ backgroundColor: "#f8f9fa" }}>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label
                                                            htmlFor="FullName"
                                                            className="form-label fw-bold mb-2 text-start w-100"
                                                        >
                                                            Full Name <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="input-group">
                                                            <span
                                                                className="input-group-text"
                                                                style={{ backgroundColor: "#ECEFF1" }}
                                                            >
                                                                <i className="bx bx-user text-primary"></i>
                                                            </span>
                                                            <input
                                                                className="form-control py-2"
                                                                type="text"
                                                                id="FullName"
                                                                placeholder="Enter Full Name"
                                                                name="FullName"
                                                                maxLength={150}
                                                                value={spocFormData.FullName}
                                                                onChange={(e) =>
                                                                    handleInputChange("FullName", e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        {spocErrors.FullName && (
                                                            <small className="text-danger">{spocErrors.FullName}</small>
                                                        )}
                                                    </div>

                                                    <div className="col-md-6 mb-3">
                                                        <label
                                                            htmlFor="MobileNumber"
                                                            className="form-label fw-bold mb-2 text-start w-100"
                                                        >
                                                            Mobile Number <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="input-group">
                                                            <span
                                                                className="input-group-text"
                                                                style={{ backgroundColor: "#ECEFF1" }}
                                                            >
                                                                <i className="bx bxs-phone text-primary"></i>
                                                            </span>
                                                            <input
                                                                className="form-control py-2"
                                                                type="text"
                                                                id="MobileNumber"
                                                                placeholder="Enter Mobile Number"
                                                                name="MobileNumber"
                                                                maxLength={10}
                                                                value={spocFormData.MobileNumber}
                                                                onChange={(e) =>
                                                                    handleInputChange("MobileNumber", e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                        {spocErrors.MobileNumber && (
                                                            <small className="text-danger">{spocErrors.MobileNumber}</small>
                                                        )}
                                                    </div>

                                                    <div className="col-md-6 mb-3">
                                                        <label
                                                            htmlFor="DateOfBirth"
                                                            className="form-label fw-bold mb-2 text-start w-100"
                                                        >
                                                            Date Of Birth
                                                        </label>
                                                        <div className="input-group">
                                                            <span
                                                                className="input-group-text"
                                                                style={{ backgroundColor: "#ECEFF1" }}
                                                            >
                                                                <i className="bx bx-calendar text-primary"></i>
                                                            </span>
                                                            <input
                                                                className="form-control py-2"
                                                                type="date"
                                                                id="DateOfBirth"
                                                                name="DateOfBirth"
                                                                value={spocFormData.DateOfBirth}
                                                                onChange={(e) =>
                                                                    handleInputChange("DateOfBirth", e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6 mb-3">
                                                        <label
                                                            htmlFor="Age"
                                                            className="form-label fw-bold mb-2 text-start w-100"
                                                        >
                                                            Age
                                                        </label>
                                                        <div className="input-group">
                                                            <span
                                                                className="input-group-text"
                                                                style={{ backgroundColor: "#ECEFF1" }}
                                                            >
                                                                <i className="bx bx-timer text-primary"></i>
                                                            </span>
                                                            <input
                                                                className="form-control py-2"
                                                                type="number"
                                                                id="Age"
                                                                name="Age"
                                                                placeholder="Enter age"
                                                                value={spocFormData.Age}
                                                                onChange={(e) =>
                                                                    handleInputChange("Age", e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6 mb-3">
                                                        <label
                                                            htmlFor="Email"
                                                            className="form-label fw-bold mb-2 text-start w-100"
                                                        >
                                                            Email
                                                        </label>
                                                        <div className="input-group">
                                                            <span
                                                                className="input-group-text"
                                                                style={{ backgroundColor: "#ECEFF1" }}
                                                            >
                                                                <i className="bx bx-envelope text-primary"></i>
                                                            </span>
                                                            <input
                                                                className="form-control py-2"
                                                                type="email"
                                                                id="Email"
                                                                placeholder="Enter Email"
                                                                name="Email"
                                                                value={spocFormData.Email}
                                                                onChange={(e) =>
                                                                    handleInputChange("Email", e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6 mb-3">
                                                        <label
                                                            htmlFor="Qualification"
                                                            className="form-label fw-bold mb-2 text-start w-100"
                                                        >
                                                            Qualification
                                                        </label>
                                                        <div className="input-group">
                                                            <span
                                                                className="input-group-text"
                                                                style={{ backgroundColor: "#ECEFF1" }}
                                                            >
                                                                <i className="bx bx-certification text-primary"></i>
                                                            </span>
                                                            <input
                                                                className="form-control py-2"
                                                                type="text"
                                                                id="Qualification"
                                                                placeholder="Enter Qualification"
                                                                name="Qualification"
                                                                value={spocFormData.Qualification}
                                                                onChange={(e) =>
                                                                    handleInputChange("Qualification", e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-12 mb-3">
                                                        <label
                                                            htmlFor="Address"
                                                            className="form-label fw-bold mb-2 text-start w-100"
                                                        >
                                                            Address
                                                        </label>
                                                        <div className="input-group">
                                                            <span
                                                                className="input-group-text"
                                                                style={{ backgroundColor: "#ECEFF1" }}
                                                            >
                                                                <i className="bx bx-map text-primary"></i>
                                                            </span>
                                                            <textarea
                                                                className="form-control py-2"
                                                                id="Address"
                                                                placeholder="Enter Address"
                                                                name="Address"
                                                                rows="2"
                                                                value={spocFormData.Address}
                                                                onChange={(e) =>
                                                                    handleInputChange("Address", e.target.value)
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-12 mb-3">
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id="IsPrimary"
                                                                        name="IsPrimary"
                                                                        checked={spocFormData.IsPrimary}
                                                                        disabled={spocFormData.IsSecondary}
                                                                        onChange={(e) =>
                                                                            handleInputChange("IsPrimary", e.target.checked)
                                                                        }
                                                                    />
                                                                    <label className="form-check-label fw-bold" htmlFor="IsPrimary">
                                                                        <i className="bx bx-star text-warning me-1"></i>
                                                                        Primary SPOC
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id="IsSecondary"
                                                                        name="IsSecondary"
                                                                        checked={spocFormData.IsSecondary}
                                                                        disabled={spocFormData.IsPrimary}
                                                                        onChange={(e) =>
                                                                            handleInputChange("IsSecondary", e.target.checked)
                                                                        }
                                                                    />
                                                                    <label className="form-check-label fw-bold" htmlFor="IsSecondary">
                                                                        <i className="bx bx-star-half text-info me-1"></i>
                                                                        Secondary SPOC
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="d-flex justify-content-between">
                                                <button
                                                    type="submit"
                                                    className="btn btn-success"
                                                    style={{ minWidth: "120px" }}
                                                    disabled={submitLoading}
                                                >
                                                    {submitLoading ? (
                                                        <div className="spinner-border spinner-border-sm text-white" role="status">
                                                            <span className="sr-only">Loading...</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <i className="bx bx-save me-1"></i>
                                                            {isEditMode ? "Update SPOC" : "Save SPOC"}
                                                        </>
                                                    )}
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => navigate(`/hospitals/details/${hospitalId}`)}
                                                >
                                                    <i className="bx bx-x me-1"></i>
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Snackbar
                            open={snackbarOpen}
                            autoHideDuration={6000}
                            onClose={handleSnackbarClose}
                            anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                            <Alert onClose={handleSnackbarClose} severity="success">
                                {snackbarMessage}
                            </Alert>
                        </Snackbar>
                    </div>
                </PageWrapper>
            </Layout>
        </>
    );
}